import { supabase } from '@/integrations/supabase/client';
import { Debt, DebtFormData } from '@/types/debt';

export interface SupabaseDebt {
  id: string;
  user_id: string;
  profile_id: string | null;
  name: string;
  category_id: string | null;
  total_amount: number;
  remaining_amount: number;
  installments: number;
  paid_installments: number;
  paid_installments_list: any; // Json type from Supabase
  start_date: string;
  due_day: number;
  status: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

const mapDebtToSupabase = (debt: Debt, userId: string, profileId: string): Omit<SupabaseDebt, 'created_at' | 'updated_at'> => {
  return {
    id: debt.id,
    user_id: userId,
    profile_id: profileId,
    name: debt.name,
    category_id: debt.categoryId,
    total_amount: debt.totalAmount,
    remaining_amount: debt.totalAmount, // Initially equal to total
    installments: debt.installments,
    paid_installments: 0,
    paid_installments_list: [],
    start_date: debt.firstDueDate,
    due_day: new Date(debt.firstDueDate).getDate(),
    status: 'ativo',
    description: debt.description || null
  };
};

const mapSupabaseToDebt = (supabaseDebt: SupabaseDebt): Debt => {
  return {
    id: supabaseDebt.id,
    name: supabaseDebt.name,
    categoryId: supabaseDebt.category_id || '',
    hasEntrance: false, // This will be determined by checking transactions
    calculationType: 'total',
    totalAmount: supabaseDebt.total_amount,
    installments: supabaseDebt.installments,
    firstDueDate: supabaseDebt.start_date,
    createdAt: supabaseDebt.created_at
  };
};

export const fetchDebts = async (userId: string, profileId: string): Promise<Debt[]> => {
  const { data, error } = await supabase
    .from('debts')
    .select('*')
    .eq('user_id', userId)
    .eq('profile_id', profileId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[supabase-debts-operations] Error fetching debts:', error);
    throw error;
  }

  return (data || []).map(mapSupabaseToDebt);
};

export const createDebt = async (debt: Debt, userId: string, profileId: string): Promise<void> => {
  const supabaseDebt = mapDebtToSupabase(debt, userId, profileId);
  
  const { error } = await supabase
    .from('debts')
    .insert(supabaseDebt);

  if (error) {
    console.error('[supabase-debts-operations] Error creating debt:', error);
    throw error;
  }
};

export const updateDebt = async (debtId: string, updates: Partial<Debt>, userId: string): Promise<void> => {
  const supabaseUpdates: Partial<SupabaseDebt> = {};
  
  if (updates.name) supabaseUpdates.name = updates.name;
  if (updates.categoryId) supabaseUpdates.category_id = updates.categoryId;
  if (updates.totalAmount !== undefined) supabaseUpdates.total_amount = updates.totalAmount;
  if (updates.installments !== undefined) supabaseUpdates.installments = updates.installments;
  if (updates.firstDueDate) {
    supabaseUpdates.start_date = updates.firstDueDate;
    supabaseUpdates.due_day = new Date(updates.firstDueDate).getDate();
  }
  if (updates.description !== undefined) supabaseUpdates.description = updates.description;

  const { error } = await supabase
    .from('debts')
    .update(supabaseUpdates)
    .eq('id', debtId)
    .eq('user_id', userId);

  if (error) {
    console.error('[supabase-debts-operations] Error updating debt:', error);
    throw error;
  }
};

export const deleteDebt = async (debtId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('debts')
    .delete()
    .eq('id', debtId)
    .eq('user_id', userId);

  if (error) {
    console.error('[supabase-debts-operations] Error deleting debt:', error);
    throw error;
  }
};
